import React, { useContext, useState } from "react";
import { useTranslation } from 'react-i18next';
import "./modern-style.css";
import "../../../styles/page-titles.css";
import "react-toastify/dist/ReactToastify.css";

import { formatDateTz } from "../../../utils/timezone";
import { SlRefresh } from "react-icons/sl";
import { FiSearch, FiActivity, FiArrowLeft } from "react-icons/fi";
import { IoWalletOutline } from "react-icons/io5";
import { BsReceipt } from "react-icons/bs";
import { HiOutlineReceiptTax } from "react-icons/hi";

import { formatPrice } from "../../../utils/formatPrice";
import { formatPriceWithSymbol } from "../../../utils/currency";
import { useNavigate, useParams } from "react-router-dom";
import { getPedido } from "../../../services/api";
import { AuthContext } from "../../../contexts/auth";
import useGetCaixaById from "../../../hooks/useGetCaixaById";
import { useMemo } from "react";

import {
  calcularDiferencasCaixa,
  calcularTotaisCaixa,
  getClasseDiferenca,
  formatarDiferenca,
  getMensagemDiferenca
} from "../../../utils/caixaCalculations";
import PermissionGate from "../../../services/PermissionGate";
import { SidebarContext } from "../../../AppRoutes";
import ModalPedido from "../../../components/ModalPedido";
import CustomTable from '../../../components/CustomTable';

import styled from 'styled-components';

const ContainerCaixa = styled.div`
  width: auto;
  min-height: 100vh;
  background: #ffffff;
  margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
  padding: 24px;
  transition: margin-left 200ms ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 880px) {
    margin-left: 0;
    padding: 16px;
  }
`

function CaixaById() {
  const { t } = useTranslation('cashRegister');
  const { caixaId } = useParams();

  const { sidebar } = useContext(SidebarContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPedido, setShowPedido] = useState(true);
  const [infoPedido, setInfoPedido] = useState({});


  const empresa = localStorage.getItem('empresa')
  const empresaParse = JSON.parse(empresa)
  const vinculo_empresa = empresaParse.cnpj;

  const navigate = useNavigate();

  const { user } = useContext(AuthContext);
  const {
    data: response,
    refetch,
    isLoading,
  } = useGetCaixaById(user._id, caixaId);

  const caixa = response?.data?.empresa;

  // Calcular diferenças dinamicamente
  const diferencas = useMemo(() => {
    if (!caixa || caixa.status_caixa) return null;
    return calcularDiferencasCaixa(caixa);
  }, [caixa]);

  const totais = useMemo(() => {
    if (!caixa || caixa.status_caixa) return null;
    return calcularTotaisCaixa(caixa);
  }, [caixa]);

  const handleShowPedido = async (pedidoId) => {
    setShowPedido(!showPedido);
    const response = await getPedido(
      user._id,
      caixa.id_empresa,
      vinculo_empresa,
      pedidoId
    );
    if (showPedido) {
      if (response.data.pedido) {
        setInfoPedido(response.data.pedido);
      }
    }
  };

  const pixValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce((acc, item) => {
      if (item.tipo_lancamento === "Pix") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa?.lancamentos_caixa]);

  const cartaoValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce((acc, item) => {
      if (item.tipo_lancamento === "Cartão") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa?.lancamentos_caixa]);

  const dinheiroValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce((acc, item) => {
      if (item.tipo_lancamento === "Dinheiro") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa?.lancamentos_caixa]);



  const suprimentosValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce((acc, item) => {
      if (item.descricao !== "Saldo Inicial" && item.valor >= 0) {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa]);

  const sangriasValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce((acc, item) => {
      if (item.descricao !== "Saldo Inicial" && item.valor <= 0) {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa]);

  // Quanto do dinheiro em caixa e gorjeta. NAO abate nada: a gorjeta esta na
  // gaveta e o operador vai conta-la junto. Este numero existe so para o dono
  // saber quanto do total pertence a equipe na hora do acerto.
  const gorjetasValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return caixa.lancamentos_caixa.reduce(
      (acc, item) => (item.tipo_operacao === 'Gorjeta' ? acc + (item.valor || 0) : acc),
      0
    );
  }, [caixa?.lancamentos_caixa]);

  const caixaValue = useMemo(() => {
    if (!caixa?.lancamentos_caixa) return 0;
    return sangriasValue + suprimentosValue + caixa.saldo_inicial;
  }, [suprimentosValue, sangriasValue, caixa]);

  const columns = [
    {
      title: t('transactions.columns.dateTime'),
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: t('transactions.columns.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('transactions.columns.value'),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: t('transactions.columns.paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      responsive: ['lg'],
    },
  ];

  const tableData = useMemo(() => {
    if(!caixa?.lancamentos_caixa) return [];

    let filteredData = caixa.lancamentos_caixa;

    // Aplicar filtro de busca se houver termo de pesquisa
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = caixa.lancamentos_caixa.filter((item) => {
        const description = item.descricao?.toLowerCase() || "";
        const paymentMethod = item.tipo_lancamento?.toLowerCase() || "";
        const value = formatPrice(Number(item.valor || 0)).toLowerCase();
        const date = formatDateTz(item.createdAt).toLowerCase();

        return description.includes(searchLower) ||
               paymentMethod.includes(searchLower) ||
               value.includes(searchLower) ||
               date.includes(searchLower);
      });
    }

    return filteredData.map((caixa) => {
      return {
        key: caixa._id,
        created_at: formatDateTz(caixa.createdAt),
        description: caixa.tipo_operacao
          ? `${caixa.descricao} (${caixa.tipo_operacao})`
          : caixa.descricao,
        value: formatPriceWithSymbol(Number(caixa.valor || 0)),
        payment_method: caixa.tipo_lancamento,
        id_pedido: caixa.id_pedido
      }
    })
  }, [caixa, searchTerm])

  return (
    <>
      <PermissionGate permissions={["default"]}>
        <ContainerCaixa sidebar={sidebar}>
          <div className="modern-caixa-container">
            {/* Header Section */}
            <div className="modern-caixa-header">
              <div className="modern-caixa-title">
                <div className="page-title-with-icon">
                  <div className="page-icon"><IoWalletOutline /></div>
                  <h1 className="page-title">
                    {t('caixaById.title', { date: formatDateTz(caixa?.data_fechamento) })}
                    <span className="status-badge closed">
                      <FiActivity />
                      {t('caixaById.closedStatus')}
                    </span>
                  </h1>
                </div>
              </div>

              {/* Controls Section */}
              <div className="modern-caixa-controls">
                <div className="modern-search-container">
                  <FiSearch className="modern-search-icon" />
                  <input
                    className="modern-search-input"
                    id="search"
                    placeholder={t('opened.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="modern-actions-container">
                  <button
                    className="modern-btn modern-btn-secondary"
                    onClick={() => {
                      process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Atualizando dados do caixa fechado...');
                      refetch();
                    }}
                  >
                    <SlRefresh />
                    {t('opened.refresh')}
                  </button>
                  <button
                    className="modern-btn modern-btn-outline"
                    onClick={() => navigate("/caixa/historico")}
                  >
                    <FiArrowLeft />
                    {t('caixaById.back')}
                  </button>
                </div>
              </div>
            </div>
            {/* Main Content */}
            {isLoading ? (
              <div className="modern-loading">
                <div className="modern-loading-spinner"></div>
                <div className="modern-loading-text">{t('opened.loading')}</div>
              </div>
            ) : (
              <div className="modern-caixa-content">
                {/* Transactions Table */}
                <div className="modern-transactions-container">
                  <div className="modern-table-header">
                    <BsReceipt />
                    <h3>{t('transactions.title')}</h3>
                    <span className="modern-table-count">
                      {t('transactions.count', { count: tableData.length })}
                    </span>
                  </div>
                  <div className="modern-table-wrapper">
                                            <CustomTable
                      className="modern-transactions-table"
                      columns={columns}
                      dataSource={tableData}
                      pagination={{
                        pageSize: 15,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} lançamentos`,
                      }}
                      rowClassName={(record) => record.id_pedido ? 'pedido-row' : ''}
                      rowHoverable={true}
                      onRow={(record) => ({
                        onClick: () => record.id_pedido && handleShowPedido(record.id_pedido)
                      })}
                      locale={{
                        emptyText: (
                          <div className="modern-empty-state">
                            <HiOutlineReceiptTax className="modern-empty-icon" />
                            <div className="modern-empty-title">
                              {t('transactions.emptyTitle')}
                            </div>
                            <div className="modern-empty-description">
                              {searchTerm.trim()
                                ? t('transactions.emptySearchDescription', { searchTerm })
                                : t('transactions.emptyDescription')
                              }
                            </div>
                          </div>
                        )
                      }}
                    />
                  </div>
                </div>

                {/* Summary Card */}
                <div className="modern-summary-card">
                  <div className="modern-summary-header">
                    <h3>{t('summary.title')}</h3>
                  </div>
                  <div className="modern-summary-content">
                    {/* Caixa Info */}
                    <div className="modern-caixa-info">
                      <div className="modern-caixa-info-item">
                        <div className="modern-caixa-info-label">{t('summary.identifier')}</div>
                        <div className="modern-caixa-info-value">{caixa._id}</div>
                      </div>
                      <div className="modern-caixa-info-item">
                        <div className="modern-caixa-info-label">{t('summary.openedAt')}</div>
                        <div className="modern-caixa-info-value">
                          {formatDateTz(caixa.createdAt)}
                        </div>
                      </div>
                      <div className="modern-caixa-info-item">
                        <div className="modern-caixa-info-label">{t('summary.closedAt')}</div>
                        <div className="modern-caixa-info-value">
                          {formatDateTz(caixa.data_fechamento)}
                        </div>
                      </div>
                    </div>

                    {/* Resumo do Caixa */}
                    <div className="modern-summary-section">
                      <h4 className="modern-summary-section-title">{t('summary.cashSummary')}</h4>
                      <div className="modern-summary-item">
                        <span className="modern-summary-label">{t('summary.initialBalance')}</span>
                        <span className="modern-summary-value">{formatPrice(caixa.saldo_inicial)}</span>
                      </div>
                      <div className="modern-summary-item">
                        <span className="modern-summary-label">{t('summary.entries')}</span>
                        <span className="modern-summary-value positive">{formatPrice(suprimentosValue)}</span>
                      </div>
                      <div className="modern-summary-item">
                        <span className="modern-summary-label">{t('summary.withdrawals')}</span>
                        <span className="modern-summary-value negative">{formatPrice(Math.abs(sangriasValue))}</span>
                      </div>
                    </div>

                    {/* Comparação de Valores - Esperado vs Informado */}
                    {!caixa.status_caixa && (caixa.valor_esperado_dinheiro !== undefined) ? (
                      <div className="modern-summary-section">
                        <h4 className="modern-summary-section-title">{t('caixaById.expectedVsReported')}</h4>
                        <div className="comparison-grid">
                          <div className="comparison-header">{t('caixaById.type')}</div>
                          <div className="comparison-header">{t('caixaById.systemExpected')}</div>
                          <div className="comparison-header">{t('caixaById.reportedValue')}</div>

                          <div className="comparison-row">
                            <span className="modern-summary-label">💰 {t('payment.cash')}</span>
                            <span className="modern-summary-value">
                              {formatPrice(diferencas?.saldo_fisico_esperado_dinheiro || caixa.valor_esperado_dinheiro || 0)}
                            </span>
                            <span className="modern-summary-value">{formatPrice(caixa.valor_informado_dinheiro || 0)}</span>
                          </div>

                          <div className="comparison-row">
                            <span className="modern-summary-label">💳 {t('payment.card')}</span>
                            <span className="modern-summary-value">{formatPrice(caixa.valor_esperado_cartao || 0)}</span>
                            <span className="modern-summary-value">{formatPrice(caixa.valor_informado_cartao || 0)}</span>
                          </div>

                          <div className="comparison-row">
                            <span className="modern-summary-label">📱 {t('payment.pix')}</span>
                            <span className="modern-summary-value">{formatPrice(caixa.valor_esperado_pix || 0)}</span>
                            <span className="modern-summary-value">{formatPrice(caixa.valor_informado_pix || 0)}</span>
                          </div>

                          <div className="comparison-total">
                            <span className="modern-summary-label">{t('caixaById.total')}</span>
                            <span className="modern-summary-value">
                              {formatPrice(
                                (diferencas?.saldo_fisico_esperado_dinheiro || caixa.valor_esperado_dinheiro || 0) +
                                (caixa.valor_esperado_cartao || 0) +
                                (caixa.valor_esperado_pix || 0)
                              )}
                            </span>
                            <span className="modern-summary-value">
                              {formatPrice(totais?.total_informado || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="modern-summary-section">
                        <h4 className="modern-summary-section-title">{t('summary.cashValues')}</h4>
                        <div className="modern-summary-item">
                          <span className="modern-summary-label">💰 {t('payment.cash')}</span>
                          <span className="modern-summary-value">{formatPrice(dinheiroValue)}</span>
                        </div>
                        <div className="modern-summary-item">
                          <span className="modern-summary-label">💳 {t('payment.card')}</span>
                          <span className="modern-summary-value">{formatPrice(cartaoValue)}</span>
                        </div>
                        <div className="modern-summary-item">
                          <span className="modern-summary-label">📱 {t('payment.pix')}</span>
                          <span className="modern-summary-value">{formatPrice(pixValue)}</span>
                        </div>
                      </div>
                    )}

                    {/* Diferenças (apenas para caixas fechados) */}
                    {!caixa.status_caixa && diferencas && (
                      <div className="modern-summary-section">
                        <h4 className="modern-summary-section-title">{t('differences.title')}</h4>
                        <p className="modern-summary-hint">
                          {t('differences.hint')}
                        </p>

                        <div className="modern-summary-item">
                          <span className="modern-summary-label">💰 {t('differences.cashDiff')}</span>
                          <span className={`modern-summary-value ${getClasseDiferenca(diferencas.diferenca_dinheiro)}`}>
                            {formatarDiferenca(diferencas.diferenca_dinheiro, formatPrice)}
                          </span>
                        </div>
                        <div className="modern-summary-item">
                          <span className="modern-summary-label">💳 {t('differences.cardDiff')}</span>
                          <span className={`modern-summary-value ${getClasseDiferenca(diferencas.diferenca_cartao)}`}>
                            {formatarDiferenca(diferencas.diferenca_cartao, formatPrice)}
                          </span>
                        </div>
                        <div className="modern-summary-item">
                          <span className="modern-summary-label">📱 {t('differences.pixDiff')}</span>
                          <span className={`modern-summary-value ${getClasseDiferenca(diferencas.diferenca_pix)}`}>
                            {formatarDiferenca(diferencas.diferenca_pix, formatPrice)}
                          </span>
                        </div>
                        <div className="modern-summary-item difference-total-row">
                          <span className="modern-summary-label"><strong>⚖️ {t('differences.totalDiff')}</strong></span>
                          <span className={`modern-summary-value ${getClasseDiferenca(diferencas.diferenca_total)}`}>
                            {diferencas.diferenca_total === 0 ? t('differences.matchesPerfectly') :
                             formatarDiferenca(diferencas.diferenca_total, formatPrice)}
                          </span>
                        </div>
                        {diferencas.diferenca_total !== 0 && (
                          <div className={`difference-message ${diferencas.diferenca_total > 0 ? 'difference-message--positive' : 'difference-message--negative'}`}>
                            {getMensagemDiferenca(diferencas.diferenca_total, formatPrice)}
                          </div>
                        )}
                      </div>
                    )}

                    {gorjetasValue > 0 && (
                      <div className="modern-summary-item">
                        <span className="modern-summary-label">{t('summary.tips', 'Gorjetas (da equipe)')}</span>
                        <span className="modern-summary-value">{formatPrice(gorjetasValue)}</span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="modern-summary-total">
                      <div className="modern-summary-label">{t('summary.totalInRegister')}</div>
                      <div className="modern-summary-value">{formatPrice(caixaValue)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ContainerCaixa>
        {infoPedido && (
          <ModalPedido
            showPedido={showPedido}
            setShowPedido={setShowPedido}
            infoPedido={infoPedido}
            showActions={false}
          />
        )}
      </PermissionGate>
    </>
  );
}

export default CaixaById;
